import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaUserTie, FaSearch } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Customers.css';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    customerType: 'individual',
    creditLimit: 0,
    paymentTerms: 'net_30',
    taxExempt: false,
    taxId: '',
    notes: '',
    status: 'active'
  });

  const customerTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'business', label: 'Business' },
    { value: 'wholesale', label: 'Wholesale' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const paymentTermsOptions = [
    { value: 'net_15', label: 'Net 15' },
    { value: 'net_30', label: 'Net 30' },
    { value: 'net_45', label: 'Net 45' },
    { value: 'net_60', label: 'Net 60' },
    { value: 'due_on_receipt', label: 'Due on Receipt' }
  ];

  useEffect(() => {
    loadCustomers();
    loadCompanies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, customerTypeFilter, statusFilter]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (customerTypeFilter) params.append('customerType', customerTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const response = await api.get(`/api/customers?${params.toString()}`);
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };



  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await api.delete(`/api/customers/${id}`);
        setCustomers(customers.filter(c => c._id !== id));
      } catch (err) {
        console.error('Error deleting customer:', err);
        setError('Failed to delete customer');
      }
    }
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      billingAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      shippingAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      customerType: 'individual',
      creditLimit: 0,
      paymentTerms: 'net_30',
      taxExempt: false,
      taxId: '',
      notes: '',
      status: 'active'
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company?._id || '',
      address: {
        street: customer.address?.street || '',
        city: customer.address?.city || '',
        state: customer.address?.state || '',
        zipCode: customer.address?.zipCode || '',
        country: customer.address?.country || ''
      },
      billingAddress: {
        street: customer.billingAddress?.street || '',
        city: customer.billingAddress?.city || '',
        state: customer.billingAddress?.state || '',
        zipCode: customer.billingAddress?.zipCode || '',
        country: customer.billingAddress?.country || ''
      },
      shippingAddress: {
        street: customer.shippingAddress?.street || '',
        city: customer.shippingAddress?.city || '',
        state: customer.shippingAddress?.state || '',
        zipCode: customer.shippingAddress?.zipCode || '',
        country: customer.shippingAddress?.country || ''
      },
      customerType: customer.customerType || 'individual',
      creditLimit: customer.creditLimit || 0,
      paymentTerms: customer.paymentTerms || 'net_30',
      taxExempt: customer.taxExempt || false,
      taxId: customer.taxId || '',
      notes: customer.notes || '',
      status: customer.status || 'active'
    });
    setShowModal(true);
    setError('');
  };

  const openViewModal = (customer) => {
    setViewingCustomer(customer);
  };

  const openLedgerForCustomer = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setViewingCustomer(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.firstName || !formData.lastName) {
        throw new Error('Please fill in all required fields');
      }

      if (editingCustomer) {
        const response = await api.put(`/api/customers/${editingCustomer._id}`, formData);
        setCustomers(customers.map(c => c._id === editingCustomer._id ? response.data.customer : c));
      } else {
        const response = await api.post('/api/customers', formData);
        setCustomers([...customers, response.data.customer]);
      }

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.firstName.toLowerCase().includes(search.toLowerCase()) ||
    c.lastName.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.customerCode && c.customerCode.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(pageStart, pageEnd);

  if (loading && customers.length === 0) return <Loading />;

  return (
    <div className="customers-container">
      <div className="customers-header">
        <div>
          <h1 className="customers-title">Customer Management</h1>
          <p className="customers-subtitle">Create, edit, and manage customer records</p>
        </div>
        <button className="add-customer-btn" onClick={openAddModal}>
          <FaPlus /> Add New Customer
        </button>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search customers by name, email, or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />

        <div className="filter-row">
          <select
            value={customerTypeFilter}
            onChange={e => setCustomerTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            {customerTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <button onClick={loadCustomers} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="customers-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredCustomers.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredCustomers.length, pageEnd)} of {filteredCustomers.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
        <table className="customers-table">
          <thead>
            <tr>
              <th>Customer Code</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Company</th>
              <th>Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>No customers found.</td>
              </tr>
            ) : (
              paginatedCustomers.map(customer => (
                <tr key={customer._id}>
                  <td>
                    <div className="customer-code-cell">
                      <FaUserTie className="customer-icon" />
                      <span>{customer.customerCode}</span>
                    </div>
                  </td>
                  <td>
                    <div className="customer-name-cell">
                      <span className="customer-name">{customer.firstName} {customer.lastName}</span>
                    </div>
                  </td>
                  <td>{customer.email}</td>
                  <td>{customer.phone || 'N/A'}</td>
                  <td>{customer.company?.name || 'N/A'}</td>
                  <td>
                    <span className={`type-badge ${customer.customerType}`}>
                      {customer.customerType}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${customer.status}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => openViewModal(customer)}
                        className="action-btn view"
                        title="View Customer"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => openEditModal(customer)}
                        className="action-btn edit"
                        title="Edit Customer"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(customer._id)}
                        className="action-btn delete"
                        title="Delete Customer"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="customer-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="customer@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <select
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a company</option>
                      {companies.map(company => (
                        <option key={company._id} value={company._id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="customerType">Customer Type</label>
                    <select
                      id="customerType"
                      name="customerType"
                      value={formData.customerType}
                      onChange={handleInputChange}
                    >
                      {customerTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Address Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="address.street">Street Address</label>
                    <input
                      type="text"
                      id="address.street"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      placeholder="123 Main St"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address.city">City</label>
                    <input
                      type="text"
                      id="address.city"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="address.state">State</label>
                    <input
                      type="text"
                      id="address.state"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      placeholder="NY"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address.zipCode">ZIP Code</label>
                    <input
                      type="text"
                      id="address.zipCode"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      placeholder="10001"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address.country">Country</label>
                  <input
                    type="text"
                    id="address.country"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    placeholder="United States"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Business Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="creditLimit">Credit Limit</label>
                    <input
                      type="number"
                      id="creditLimit"
                      name="creditLimit"
                      value={formData.creditLimit}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="paymentTerms">Payment Terms</label>
                    <select
                      id="paymentTerms"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                    >
                      {paymentTermsOptions.map(term => (
                        <option key={term.value} value={term.value}>
                          {term.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="taxId">Tax ID</label>
                    <input
                      type="text"
                      id="taxId"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      placeholder="Tax identification number"
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="taxExempt"
                        checked={formData.taxExempt}
                        onChange={handleInputChange}
                      />
                      Tax Exempt
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter customer notes"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingCustomer ? 'Update Customer' : 'Add Customer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingCustomer && (
        <div className="modal-overlay" onClick={() => setViewingCustomer(null)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer Details - {viewingCustomer.customerCode}</h2>
              <button className="modal-close" onClick={() => setViewingCustomer(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="customer-view-content">
              <div className="customer-info-section">
                <div className="detail-group">
                  <h3>Basic Information</h3>
                  <div className="detail-item">
                    <label>Customer Code:</label>
                    <span>{viewingCustomer.customerCode}</span>
                  </div>
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{viewingCustomer.firstName} {viewingCustomer.lastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{viewingCustomer.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{viewingCustomer.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{viewingCustomer.company?.name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Customer Type:</label>
                    <span className={`type-badge ${viewingCustomer.customerType}`}>
                      {viewingCustomer.customerType}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingCustomer.status}`}>
                      {viewingCustomer.status}
                    </span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Business Information</h3>
                  <div className="detail-item">
                    <label>Credit Limit:</label>
                    <span>${viewingCustomer.creditLimit?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Terms:</label>
                    <span>{viewingCustomer.paymentTerms || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tax Exempt:</label>
                    <span>{viewingCustomer.taxExempt ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tax ID:</label>
                    <span>{viewingCustomer.taxId || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="address-section">
                <h3>Address Information</h3>
                <div className="address-details">
                  <div className="detail-item">
                    <label>Street:</label>
                    <span>{viewingCustomer.address?.street || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>City:</label>
                    <span>{viewingCustomer.address?.city || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>State:</label>
                    <span>{viewingCustomer.address?.state || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>ZIP Code:</label>
                    <span>{viewingCustomer.address?.zipCode || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Country:</label>
                    <span>{viewingCustomer.address?.country || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {viewingCustomer.notes && (
                <div className="notes-section">
                  <h3>Notes</h3>
                  <p>{viewingCustomer.notes}</p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
                <button className="btn-primary" onClick={openLedgerForCustomer}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )
      }

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingCustomer ? `Customer ${viewingCustomer.firstName} ${viewingCustomer.lastName}` : ''}
      />
    </div >
  );
};

export default Customers;
